import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/Button';
import { ArrowLeft, ArrowRight, Target, ExternalLink, X } from 'lucide-react';
import { GoalSelect } from '../components/GoalSelect';
import { useEncryption } from '../hooks/useEncryption';
import type { JournalFormData } from '../types/journal';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { getAllLowestRungSteps } from '../lib/ladderUtils';
import type { CurrentFocusStep } from '../types/ladder';

export function Journal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentQuestion, setCurrentQuestion] = useState(location.state?.startAtQuestion || 0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [allLowestSteps, setAllLowestSteps] = useState<CurrentFocusStep[]>([]);
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
  const [useCustomGoal, setUseCustomGoal] = useState(false);
  const [entry, setEntry] = useState<JournalFormData>({
    celebration: location.state?.celebration || '',
    celebrationLearning: '',
    goal: '',
    goalImportance: '',
    reflection: '',
    valueAlignment: '',
    action: ''
  });
  const { encrypt } = useEncryption();
  const goalSelectRef = useRef<HTMLDivElement>(null);

  // Fetch core values and current focus step on mount
  React.useEffect(() => {
    const fetchCoreValues = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('core_value_1, core_value_2, core_value_3')
          .eq('id', user.id)
          .single();

        if (profile) {
          setCoreValues([
            profile.core_value_1,
            profile.core_value_2,
            profile.core_value_3
          ].filter(Boolean));
        }
      } catch (error) {
        console.error('Error fetching core values:', error);
      }
    };

    const fetchLowestSteps = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const lowestSteps = await getAllLowestRungSteps(user.id);
        setAllLowestSteps(lowestSteps);

        if (lowestSteps.length > 0) {
          setSelectedStepIndex(0);
          setEntry(prev => ({ ...prev, goal: lowestSteps[0].step.description }));
        }
      } catch (error) {
        console.error('Error fetching lowest rung steps:', error);
      }
    };

    fetchCoreValues();
    fetchLowestSteps();
  }, []);

  const questions = React.useMemo(() => [
    {
      id: 'celebration',
      text: 'What can you celebrate about yesterday?',
      placeholder: 'Think about your wins, progress, and things that went well.'
    },
    {
      id: 'celebrationLearning',
      text: 'What did you learn from that?',
      placeholder: 'Reflect on the lessons and insights from your celebration.'
    },
    {
      id: 'goal',
      text: 'What is your goal?',
      placeholder: 'What do you want to accomplish or solve?'
    },
    {
      id: 'reflection',
      text: `What fear comes up when you think of ${entry.goal ? entry.goal : 'that goal'}?`,
      placeholder: 'Write it out like it sounds in your head.'
    },
    {
      id: 'valueAlignment',
      text: `What do your core values (${coreValues.join(', ')}) say to do in this situation?`,
      placeholder: 'How can you align your actions with your values?'
    },
    {
      id: 'action',
      text: 'From that list, what actions will you take today?',
      placeholder: 'List specific, actionable steps (one per line).'
    }
  ], [coreValues, entry.goal]);

  const handleAnswer = useCallback(async (answer: string) => {
    // Set start time when submitting first question
    if (currentQuestion === 0 && !startTime) {
      setStartTime(new Date());
    }

    const currentField = questions[currentQuestion].id as keyof JournalFormData;

    // Update the entry state with the new answer
    setEntry(prev => ({
      ...prev,
      [currentField]: answer
    }));

    // If this is the final question (actions)
    if (currentQuestion === questions.length - 1) {
      const actionsList = answer.split('\n').filter(action => action.trim());
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/');
          return;
        }

        // Encrypt all journal entry fields
        const encryptedEntry = {
          user_id: session.user.id,
          date: new Date(),
          celebration: await encrypt(entry.celebration),
          celebration_learning: await encrypt(entry.celebrationLearning),
          goal: await encrypt(entry.goal),
          goal_importance: await encrypt(entry.goalImportance),
          reflection: await encrypt(entry.reflection),
          value_alignment: await encrypt(entry.valueAlignment),
          action: await encrypt(actionsList.join('\n')),
          start_time: startTime,
          end_time: new Date()
        };

        const { error: journalError } = await supabase
          .from('journal_entries')
          .insert([encryptedEntry]);

        if (journalError) throw journalError;

        // Upsert growth_days entry for today's journal
        const today = new Date().toISOString().slice(0, 10);

        await supabase
          .from('growth_days')
          .upsert([
            {
              user_id: session.user.id,
              date: today,
              journaled: true,
              updated_at: new Date().toISOString()
            }
          ], { onConflict: ['user_id', 'date'] });


        // Create tasks from actions
        const tasks = actionsList.map(action => ({
          user_id: session.user.id,
          text: action.trim(),
          completed: false
        }));

        if (tasks.length > 0) {
          const { error: tasksError } = await supabase
            .from('tasks')
            .insert(tasks);

          if (tasksError) throw tasksError;
        }

        // Add points for completing journal entry
        const { error: scoreError } = await supabase
          .from('score_history')
          .insert([{
            user_id: session.user.id,
            points: 20,
            type: 'journal',
            description: 'Completed daily journal entry'
          }]);

        if (scoreError) throw scoreError;

        // Navigate to completion page
        navigate('/journal/complete', {
          state: {
            tasks: actionsList,
            goal: entry.goal,
            goalImportance: entry.goalImportance
          }
        });
      } catch (error: any) {
        console.error('Error saving journal entry:', error);
      }
    } else {
      // Move to next question
      setCurrentQuestion(prev => prev + 1);
    }
  }, [currentQuestion, entry, navigate, questions, startTime, encrypt]);

  const saveGoalAndContinue = async () => {
    if (!entry.goal.trim()) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if this goal already exists
      const { data: existingGoals } = await supabase
        .from('previous_goals')
        .select('id, text, use_count')
        .eq('user_id', user.id)
        .ilike('text', entry.goal)
        .maybeSingle();

      if (existingGoals) {
        // Update existing goal
        await supabase
          .from('previous_goals')
          .update({
            use_count: existingGoals.use_count + 1,
            last_used_at: new Date().toISOString()
          })
          .eq('id', existingGoals.id);
      } else {
        // Insert new goal
        await supabase
          .from('previous_goals')
          .insert({
            text: entry.goal.trim(),
            user_id: user.id,
            status: 'active'
          });
      }

      // Move to next question
      setCurrentQuestion(prev => prev + 1);
    } catch (error) {
      console.error('Error saving goal:', error);
      // Still move to next question even if there's an error saving the goal
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const currentQ = questions[currentQuestion];

  return (
    <ProtectedLayout>
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#004060]">Alignment Engine</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
              aria-label="Back to Dashboard"
              title="Back to Dashboard"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="hidden lg:flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          <div className="space-y-6">
            {currentQ.id === 'goal' ? (
              <div ref={goalSelectRef}>
                <h2 className="text-xl font-semibold text-gray-700 mb-3">
                  {currentQ.text}
                </h2>

                {allLowestSteps.length > 0 && !useCustomGoal ? (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      {allLowestSteps.map((focusStep, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setSelectedStepIndex(index);
                            setEntry(prev => ({ ...prev, goal: focusStep.step.description }));
                          }}
                          className={`cursor-pointer rounded-lg p-4 border-2 transition-colors ${
                            selectedStepIndex === index
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-white border-gray-200 hover:border-blue-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Target className={`w-5 h-5 mt-0.5 ${
                              selectedStepIndex === index ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-gray-500 mb-1">
                                {focusStep.goal.name}
                              </p>
                              <p className={`font-medium ${
                                selectedStepIndex === index ? 'text-gray-900' : 'text-gray-700'
                              }`}>
                                {focusStep.step.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => navigate('/dash-growth-loop')}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      View in Planner
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setUseCustomGoal(true)}
                      className="text-sm text-gray-600 hover:text-gray-800 underline block"
                    >
                      Use a different goal instead
                    </button>
                  </div>
                ) : (
                  <div>
                    <GoalSelect
                      value={entry.goal}
                      onChange={(value) => setEntry(prev => ({ ...prev, goal: value }))}
                      isFirstTime={false}
                      className="mb-4"
                    />
                    {allLowestSteps.length > 0 && (
                      <button
                        onClick={() => {
                          setUseCustomGoal(false);
                          setSelectedStepIndex(0);
                          setEntry(prev => ({ ...prev, goal: allLowestSteps[0].step.description }));
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        Use my Action Ladder steps instead
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold text-gray-700 mb-3">
                  {currentQ.text}
                </h2>
                <textarea
                  value={entry[currentQ.id as keyof JournalFormData] as string}
                  onChange={(e) => setEntry(prev => ({ ...prev, [currentQ.id]: e.target.value }))}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent resize-none h-32"
                  placeholder={currentQ.placeholder}
                />
              </div>
            )}

            <div className="flex justify-between items-center">
              {currentQuestion > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestion(prev => prev - 1)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </Button>
              )}
              
              <div className="flex-1 text-center text-gray-500">
                Question {currentQuestion + 1} of {questions.length}
              </div>

              <Button
                onClick={() => currentQ.id === 'goal' ? 
                  saveGoalAndContinue() : 
                  handleAnswer(entry[currentQ.id as keyof JournalFormData] as string)
                }
                disabled={!(entry[currentQ.id as keyof JournalFormData] as string)?.trim()}
                className="flex items-center gap-2"
              >
                {currentQuestion === questions.length - 1 ? 'Complete' : 'Next'}
                {currentQuestion < questions.length - 1 && <ArrowRight className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </ProtectedLayout>
  );
}